/**
 * `entities/project-artifact` API surface.
 *
 * Mirrors `entities/project-folder/api/project-folder.api.ts` 1:1 — service
 * function + `queryOptions` helper for TanStack consumers (reads flow through
 * queryOptions to enforce the cache-key contract).
 *
 * Mutations (attach / detach) live in `features/manage-project-artifacts`;
 * this slice is read-only.
 */
import { ProjectArtifactsService } from '@inference.engine/workspace-api-client/web';
import { queryOptions } from '@tanstack/react-query';
import { workspaceHttp } from '@/lib/api/http-client';
import type { ListProjectArtifactsResponse, ProjectArtifactsListQueryParams } from '../model/types';
import { PROJECT_ARTIFACT_QUERY_KEYS } from './query-keys';

export const listProjectArtifacts = async (
  projectId: string,
  query?: ProjectArtifactsListQueryParams,
): Promise<ListProjectArtifactsResponse> => {
  const response = await ProjectArtifactsService.list({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    query,
  });

  if (!response.data) {
    throw new Error('Failed to list project artifacts');
  }

  return response.data;
};

export const projectArtifactsQuery = (
  projectId: string | undefined,
  query?: ProjectArtifactsListQueryParams,
) =>
  queryOptions({
    queryKey: PROJECT_ARTIFACT_QUERY_KEYS.list(projectId!, query),
    queryFn: () => listProjectArtifacts(projectId!, query),
    enabled: !!projectId,
  });

/**
 * `GET /projects/:projectId/artifacts/ids` — every live attachment id for a
 * project, without pagination. Lets the FE picker compute the disabled set
 * for "already attached" rows even when projects exceed the list endpoint's
 * `take` ceiling.
 */
export const listProjectAttachedArtifactIds = async (projectId: string): Promise<string[]> => {
  const take = 200;
  const artifactIds: string[] = [];
  let skip = 0;
  let total = 0;

  do {
    const response = await ProjectArtifactsService.list({
      client: workspaceHttp,
      path: { platformProjectId: projectId },
      query: { skip, take },
    });
    if (!response.data) {
      throw new Error('Failed to list project attached artifact IDs');
    }
    for (const item of response.data.items) {
      if (item.artifactId) artifactIds.push(item.artifactId);
    }
    total = response.data.total;
    skip += take;
  } while (skip < total);

  return artifactIds;
};

export const projectAttachedArtifactIdsQuery = (projectId: string | undefined) =>
  queryOptions({
    queryKey: PROJECT_ARTIFACT_QUERY_KEYS.attachedIds(projectId!),
    queryFn: () => listProjectAttachedArtifactIds(projectId!),
    enabled: !!projectId,
  });
